// copied from API (IntegrationBusiness):

// Note: The following enum type is actually not needed for the frontend but used in tests.
import { ResourceTypes } from '@/core/models/integration-types';

export enum AvailableIntegrations {
    Lexware = 'lexware',
    Sevdesk = 'sevdesk',
    FastBill = 'fastbill',
    Bexio = 'bexio',
    Meinbuero = 'meinbuero',
    Orgamax = 'orgamax',
}

export const AVAILABLE_INTEGRATIONS = Object.values(AvailableIntegrations);

export function isAvailableIntegration(value: string): value is AvailableIntegrations {
    return AVAILABLE_INTEGRATIONS.includes(value as AvailableIntegrations);
}

export type IntegrationStatus = 'available' | 'active' | 'unavailable';

export type IntegrationProperties = AccountingIntegrationProperties; // add other property specs here

export type IntegrationAuthorizationMethod = 'oauth2' | 'apiKey' | 'apiKeyWithEmail' | 'iid';

export interface AccountingIntegrationProperties {
    authorizationMethod: IntegrationAuthorizationMethod;
    /** Resource types that support webhook-based auto-sync. Replaces the removed `supportsAutomaticSync` boolean. */
    autoSyncResourceTypes: ResourceTypes[];
    supportedResourceTypes: ResourceTypes[];
    hourUnitName: string;
    supportsUnitRequest: boolean;
    supportsUnitMapping: boolean;
}

export enum AutoImportWebHookEventTypes {
    CONTACT_CREATED = 'contact.created',
    SERVICE_CREATED = 'article.created', // TODO lexware uses "article.created" - must be renamed!
    NONE = 'none',
}

export type UnitMapping = Record<string, string>;

export interface IntegrationConfiguration {
    apiKey?: string;
    email?: string;
    refreshToken?: string;
    autoImport?: AutoImportConfig;
    unitMapping?: UnitMapping;
}

export interface LayerImportConfig {
    layerId: number;
    layerIndex: number;
    autoImportEnabled: boolean;
    // TODO the importEventType should not be stored here, just the resource for mapping... How the mapping is done is a service specific implementation detail!
    importEventType: AutoImportWebHookEventTypes; // Only one event type per layer as this is webhook-based
    subscriptionId: string | null; // Subscription ID for the webhook, used to delete the webhook subscription
    callbackUrl: string | null; // Callback URL is used for resubscription of webhooks when the user reauthenticates
}

export interface AutoImportConfig {
    layers: LayerImportConfig[];
}

// export type IntegrationConfiguration = Record<string, unknown>;

/**
 * 'rate-limited' means auth could not be verified because the integration's API rejected the check due to
 * rate limiting, not because the credentials are actually invalid - do not prompt for re-authentication in
 * this case, surface it as a temporary, actionable state instead.
 */
export type AuthStatus = 'valid' | 'invalid' | 'rate-limited';

export class IntegrationConfig {
    service!: AvailableIntegrations;
    status?: IntegrationStatus;
    properties?: IntegrationProperties;
    configuration?: IntegrationConfiguration;
    authStatus?: AuthStatus;
}

// accounting integration (copied from backend):

/**
 * Contact Model
 * It represents the necessary data of contacts that we need for invoice creation.
 */
export interface Contact {
    id: string; // can save numbers (sevdesk) and uuid strings (lexware)
    name: string;
    link: string; // link to contact in external system (sevdesk, lexware ...)
    flags?: {
        noAddr: boolean;
    };
}

export type ServiceResource = Contact;
export type IntegrationResource = Contact;

export interface IntegrationUnit {
    id: string; // can save numbers (sevdesk) and uuid strings (lexware)
    name: string;
}
